import type { RawStorefrontProduct, RawStorefrontVariant } from '@/core/product'
import type { Product, ProductImage, ProductVariant } from '@/services/products'

type ProductWithCategory = Product & {
  category?: { id: string; name: string; slug: string } | null
}

function mapVariant(v: ProductVariant, productId: string): RawStorefrontVariant {
  return {
    id: v.id,
    product_id: v.product_id ?? productId,
    attributes: (v.attributes ?? {}) as Record<string, string>,
    price: v.price ?? null,
    stock: v.stock ?? null,
    inventory_quantity: v.inventory_quantity ?? null,
    status: v.status,
    is_default: v.is_default === true,
    sku: v.sku ?? null,
    image_url: v.image_url ?? null,
  }
}

function sortedProductImages(rows: ProductImage[] | undefined): { url: string; position?: number }[] {
  if (!rows?.length) return []
  return [...rows]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((r) => ({ url: r.url, position: r.position }))
}

function isDropshippingProduct(p: ProductWithCategory): boolean {
  return (
    p.is_dropshipping === true ||
    String(p.product_type ?? '').toLowerCase() === 'dropshipping' ||
    String(p.import_source ?? '').toLowerCase() === 'aliexpress' ||
    (typeof p.supplier_url === 'string' && p.supplier_url.trim() !== '')
  )
}

/**
 * Maps catalog `Product` rows (list/search payloads) into `RawStorefrontProduct`
 * for `normalizeStorefrontProduct` + cart (`addToCart`).
 */
export function catalogProductToRawStorefront(p: ProductWithCategory): RawStorefrontProduct {
  const imgs = sortedProductImages(p.product_images)
  const cat = p.category
  const isDropshipping = isDropshippingProduct(p)
  return {
    id: p.id,
    store_id: p.store_id,
    name: p.name,
    description: p.meta_description,
    meta_description: p.meta_description,
    slug: p.slug,
    image: p.cover_image ?? p.image,
    images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
    product_images: imgs.length ? imgs : undefined,
    has_variants: p.has_variants === true,
    variants: (p.variants ?? []).map((v) => mapVariant(v, p.id)),
    default_variant_id: p.default_variant_id ?? null,
    price: p.price,
    compare_at_price: p.compare_at_price,
    stock: p.stock,
    track_inventory: p.track_inventory,
    is_dropshipping: isDropshipping,
    status: p.status,
    category_id: p.category_id,
    categories: cat ? { id: cat.id, name: cat.name, slug: cat.slug } : undefined,
    product_type: p.product_type ?? null,
    digital_file_url: p.digital_file_url ?? null,
    import_source: p.import_source ?? null,
    size_chart: p.size_chart ?? undefined,
  }
}
